import { Directive, ElementRef, EventEmitter, NgZone, Output, ViewChild } from '@angular/core';
import { of } from 'rxjs';
import { CdkConnectedOverlay, OverlayRef } from '@angular/cdk/overlay';
import { TranslateService } from '@ngx-translate/core';
import { delay, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FilterSelectOption } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-select.model';
import { FilterBase } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-base';

@UntilDestroy()
@Directive()
export abstract class FilterSelectBase<TFilter> extends FilterBase<string[], TFilter> {
  @ViewChild('filterOverlay')
  public filterOverlayRef: ElementRef<HTMLDivElement>;

  @Output()
  public searchControlValueChanged: EventEmitter<string> = new EventEmitter();

  private resizeObserver: ResizeObserver;
  private overlayHostElement: HTMLElement;
  protected overlayRef: OverlayRef;
  protected valuesEmpty: string[][] = [[]];

  public constructor(
    protected readonly translateService: TranslateService,
    protected readonly ngZone: NgZone,
  ) {
    super();
  }

  protected abstract getSummary(): string;

  public onSearchControlValueChanged(val): void {
    this.searchControlValueChanged.emit(val);
  }

  public onSelectionChanged(ev: FilterSelectOption[]): void {
    this.filterValueChanged.emit(ev.map((o) => o.id));
    // skip one tick to make sure that overlay is rendered
    of({})
      .pipe(take(1), untilDestroyed(this), delay(0))
      .subscribe(() => {
        this.overlayRef.updatePosition();
      });
  }

  public onAttach(overlay: CdkConnectedOverlay): void {
    this.overlayHostElement = overlay.overlayRef.hostElement;

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.updateOverlayMaxHeight());

      this.resizeObserver.observe(this.overlayHostElement);
    });

    this.overlayRef = overlay.overlayRef;
  }

  public onDetach(): void {
    this.resizeObserver?.disconnect();
    this.overlayHostElement = null;
    this.overlayRef = null;
  }

  protected updateOverlayMaxHeight(): void {
    if (!this.filterOverlayRef || !this.overlayHostElement) {
      return;
    }

    const rect = this.filterOverlayRef.nativeElement.getBoundingClientRect();
    const height = Math.max(0, this.overlayHostElement.clientHeight - rect.top);
    this.filterOverlayRef.nativeElement.style.maxHeight = `${height}px`;
  }

  protected onFilterValueChanged(): void {}
}
